import React, { useState, useEffect, useRef } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  Shield, 
  Menu, 
  X, 
  User, 
  ChevronDown,
  LogOut,
  Settings,
  Bell,
  Home,
  ClipboardCheck,
  BarChart3,
  FileText,
  GraduationCap,
  AlertTriangle,
  Calendar,
  Activity,
  Clock,
  Eye,
  Building2
} from 'lucide-react'
import { useAuth } from '../context/AuthContext'
import { supabase } from '../lib/supabase'
import { getUserTypeLabel as getTypeLabel, getUserTypeColor as getTypeColor } from '../utils/userTypeSync'

const Navbar = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false)
  const [isNotificationDropdownOpen, setIsNotificationDropdownOpen] = useState(false)
  const [incidents, setIncidents] = useState([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [isBlinking, setIsBlinking] = useState(false)
  const [isRedAlert, setIsRedAlert] = useState(false)
  const [selectedIncident, setSelectedIncident] = useState(null)
  const [showIncidentModal, setShowIncidentModal] = useState(false)
  const [autoSyncStatus, setAutoSyncStatus] = useState('connecting')
  const [readIncidents, setReadIncidents] = useState(new Set())
  const [connectionError, setConnectionError] = useState(null)
  const location = useLocation()
  const { user, profile, signOut } = useAuth()
  
  // Use refs to store timeout IDs to prevent them from being cleared on re-renders
  const redAlertTimeoutRef = useRef(null)
  const blinkingTimeoutRef = useRef(null)
  const periodicSyncRef = useRef(null)
  const subscriptionRef = useRef(null)
  const reconnectAttemptsRef = useRef(0)
  
  // Load read incidents from localStorage on mount (user-specific)
  useEffect(() => {
    if (!user?.id) return
    
    const userSpecificKey = `readIncidents_${user.id}`
    const storedReadIncidents = localStorage.getItem(userSpecificKey)
    if (storedReadIncidents) {
      try {
        const readData = JSON.parse(storedReadIncidents)
        // Filter out old read incidents (older than 7 days) to prevent localStorage bloat
        const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000
        const validReadIncidents = readData.filter(item => {
          return item.timestamp && item.timestamp > sevenDaysAgo
        })
        
        const readIds = validReadIncidents.map(item => item.id)
        setReadIncidents(new Set(readIds))
        
        // Update localStorage with cleaned data
        localStorage.setItem(userSpecificKey, JSON.stringify(validReadIncidents))
        console.log('📖 Loaded read incidents from localStorage:', readIds.length)
      } catch (error) {
        console.error('Error loading read incidents from localStorage:', error)
        // Clear corrupted data
        localStorage.removeItem(userSpecificKey)
      }
    }
  }, [user?.id])

  // Debug: Log isRedAlert state changes
  useEffect(() => {
    console.log('🔴 DEBUG: isRedAlert state changed to:', isRedAlert)
  }, [isRedAlert])
  
  // Test function for debugging notifications (accessible via window.testNotification)
  useEffect(() => {
    window.testNotification = (severity = 'high') => {
      console.log(`🧪 Testing notification system with ${severity} severity...`)
      setUnreadCount(prev => prev + 1)
      setIsBlinking(true)
      
      // Check if this should trigger red alert
      const isAlertIncident = severity === 'critical' || severity === 'high'
      
      if (isAlertIncident) {
        setIsRedAlert(true)
        console.log('🚨 TEST RED ALERT: Testing critical/high severity animation!')
        console.log('🔍 Current isRedAlert state:', true)
        
        // Clear any existing red alert timeout
        if (redAlertTimeoutRef.current) {
          clearTimeout(redAlertTimeoutRef.current)
        }
        
        // Red alert animation for 20 seconds
        redAlertTimeoutRef.current = setTimeout(() => {
          setIsRedAlert(false)
          console.log('⏰ Test red alert animation stopped after 20 seconds')
          console.log('🔍 Current isRedAlert state:', false)
          redAlertTimeoutRef.current = null
        }, 20000)
      }
      
      // Add a test incident
      const testIncident = {
        id: 'test-' + Date.now(),
        title: `Test ${severity.toUpperCase()} Security Incident`,
        severity: severity,
        status: 'submitted',
        created_at: new Date().toISOString(),
        users: {
          name: 'Test User',
          email: 'test@example.com'
        }
      }
      
      setIncidents(prev => [testIncident, ...prev.slice(0, 9)])
      
      // Clear any existing blinking timeout
      if (blinkingTimeoutRef.current) {
        clearTimeout(blinkingTimeoutRef.current)
      }
      
      // Stop blinking after 15 seconds
      blinkingTimeoutRef.current = setTimeout(() => {
        setIsBlinking(false)
        console.log('⏰ Test notification blinking stopped')
        blinkingTimeoutRef.current = null
      }, 15000)
      
      console.log(`✅ Test ${severity} notification triggered!`)
    }
    
    // Add test functions for different severities
    window.testCriticalAlert = () => window.testNotification('critical')
    window.testHighAlert = () => window.testNotification('high')
    window.testMediumAlert = () => window.testNotification('medium')
    window.testLowAlert = () => window.testNotification('low')
    
    // Direct state test functions
    window.forceRedAlert = () => {
      console.log('🔴 FORCING RED ALERT STATE TO TRUE')
      setIsRedAlert(true)
      console.log('🔍 Forced isRedAlert state:', true)
    }
    
    window.stopRedAlert = () => {
      console.log('🟢 STOPPING RED ALERT STATE')
      setIsRedAlert(false)
      console.log('🔍 Stopped isRedAlert state:', false)
    }
    
    window.checkRedAlertState = () => {
      console.log('🔍 Current isRedAlert state:', isRedAlert)
      console.log('🔍 Current isBlinking state:', isBlinking)
      return { isRedAlert, isBlinking }
    }
    
    // Real-time subscription debugging functions
    window.testRealtimeConnection = async () => {
      console.log('🧪 Testing real-time connection...')
      try {
        const { data, error } = await supabase
          .from('incidents')
          .insert({
            ticket_number: `TEST-${Date.now()}`,
            incident_type: 'malware',
            severity: 'critical',
            title: 'Test Critical Incident for Animation',
            description: 'This is a test incident to verify real-time notifications',
            incident_date_time: new Date().toISOString(),
            location: 'Test Location',
            reporter_name: 'Test User',
            reporter_email: 'test@example.com',
            department: 'IT',
            agency: 'Test Agency',
            reported_by: user?.id || '00000000-0000-0000-0000-000000000000'
          })
          .select()
          .single()
        
        if (error) {
          console.error('❌ Test incident creation failed:', error)
          return { success: false, error }
        }
        
        console.log('✅ Test incident created successfully:', data)
        console.log('🔔 Watch for real-time notification above...')
        return { success: true, data }
      } catch (err) {
        console.error('❌ Test failed with exception:', err)
        return { success: false, error: err }
      }
    }
    
    window.checkSubscriptionStatus = () => {
      console.log('📡 Current subscription status:', autoSyncStatus)
      console.log('📊 Current incidents count:', incidents.length)
      console.log('🔔 Current unread count:', unreadCount)
      console.log('🚨 Is red alert active:', isRedAlert)
      console.log('🔴 Is blinking active:', isBlinking)
      console.log('⏰ Red alert timeout active:', !!redAlertTimeoutRef.current)
      console.log('⏰ Blinking timeout active:', !!blinkingTimeoutRef.current)
      console.log('📖 Read incidents count:', readIncidents.size)
      return { autoSyncStatus, incidentsCount: incidents.length, unreadCount, isRedAlert, isBlinking, readIncidentsCount: readIncidents.size }
    }
    
    window.clearReadIncidents = () => {
      console.log('🗑️ Clearing all read incidents from localStorage...')
      setReadIncidents(new Set())
      localStorage.removeItem('readIncidents')
      console.log('✅ Read incidents cleared')
    }
    
    window.forceReconnectRealtime = () => {
      console.log('🔄 Forcing real-time reconnection...')
      
      // Clean up existing subscription
      if (subscriptionRef.current) {
        try {
          subscriptionRef.current.unsubscribe()
          console.log('🧹 Existing subscription cleaned up')
        } catch (error) {
          console.warn('⚠️ Error cleaning up subscription:', error)
        }
        subscriptionRef.current = null
      }
      
      // Reset reconnection attempts
      reconnectAttemptsRef.current = 0
      
      // Update status and recreate subscription
      setAutoSyncStatus('reconnecting')
      
      // Create new subscription after a brief delay
      setTimeout(() => {
        createSubscription()
      }, 1000)
      
      console.log('✅ Reconnection triggered - check console for subscription status')
    }
    
    window.testRedAlertDirectly = () => {
      console.log('🚨 Testing red alert animation directly...')
      
      setIsRedAlert(true)
      setIsBlinking(true)
      setUnreadCount(prev => prev + 1)
      
      console.log('✅ Red alert and blinking activated for 20 seconds')
    }
    
    window.createTestIncident = async () => {
      console.log('🧪 Creating test incident for debugging...')
      try {
        const { data, error } = await supabase
          .from('incidents')
          .insert({
            title: 'Test Incident - ' + new Date().toLocaleTimeString(),
            description: 'This is a test incident created for debugging the notification system.',
            incident_type: 'malware_infection',
            severity: 'high',
            status: 'submitted',
            incident_date_time: new Date().toISOString(),
            location: 'Test Location',
            reporter_name: 'Test User',
            reporter_email: 'test@example.com',
            department: 'IT',
            agency: 'Test Agency',
            reported_by: user?.id || '00000000-0000-0000-0000-000000000000'
          })
          .select()
          .single()
        
        if (error) {
          console.error('❌ Test incident creation failed:', error)
          return { success: false, error }
        }
        
        console.log('✅ Test incident created successfully:', data)
        return { success: true, data }
      } catch (err) {
        console.error('❌ Test failed with exception:', err)
        return { success: false, error: err }
      }
    }
    
    window.debugNotificationState = () => {
      console.log('🔍 NOTIFICATION DEBUG STATE:')
      console.log('- Unread Count:', unreadCount)
      console.log('- Total Incidents:', incidents.length)
      console.log('- Read Incidents:', readIncidents.size)
      console.log('- User Type:', profile?.user_type)
      console.log('- Is Red Alert:', isRedAlert)
      console.log('- Is Blinking:', isBlinking)
      console.log('- Auto Sync Status:', autoSyncStatus)
      console.log('- Recent Incidents (last 2 hours):')
      
      const twoHoursAgo = new Date(Date.now() - 2 * 60 * 60 * 1000)
      incidents.forEach(incident => {
        const incidentTime = new Date(incident.created_at)
        const isRecent = incidentTime > twoHoursAgo
        const isRead = readIncidents.has(incident.id)
        console.log(`  - ${incident.title}: Created ${incident.created_at}, Recent: ${isRecent}, Read: ${isRead}, Status: ${incident.status}`)
      })
      
      return {
        unreadCount,
        totalIncidents: incidents.length,
        readIncidents: readIncidents.size,
        userType: profile?.user_type,
        isRedAlert,
        isBlinking,
        autoSyncStatus
      }
      
      // Clear any existing timeouts
      if (redAlertTimeoutRef.current) {
        clearTimeout(redAlertTimeoutRef.current)
      }
      if (blinkingTimeoutRef.current) {
        clearTimeout(blinkingTimeoutRef.current)
      }
      
      // Set new timeouts
      redAlertTimeoutRef.current = setTimeout(() => {
        setIsRedAlert(false)
        console.log('⏰ Red alert animation stopped')
        redAlertTimeoutRef.current = null
      }, 20000)
      
      blinkingTimeoutRef.current = setTimeout(() => {
        setIsBlinking(false)
        console.log('⏰ Blinking animation stopped')
        blinkingTimeoutRef.current = null
      }, 15000)
    }
    
    // Enhanced diagnostic functions
    window.diagnoseWebSocketIssues = () => {
      console.log('🔍 Diagnosing WebSocket connection issues...')
      
      // Check browser WebSocket support
      if (!('WebSocket' in window)) {
        console.error('❌ WebSocket not supported in this browser')
        return
      }
      
      console.log('✅ WebSocket API is supported')
      
      // Test direct WebSocket connection to Supabase
      const testWs = new WebSocket('wss://uaooghxtslvyaizttavi.supabase.co/realtime/v1/websocket')
      
      testWs.onopen = () => {
        console.log('✅ Direct WebSocket connection successful')
        testWs.close()
      }
      
      testWs.onerror = (error) => {
        console.error('❌ Direct WebSocket connection failed:', error)
      }
      
      testWs.onclose = (event) => {
        console.log(`🔌 WebSocket closed with code: ${event.code}, reason: ${event.reason}`)
      }
      
      // Check current subscription state
      console.log('📊 Current subscription state:', {
        subscription: subscriptionRef.current ? 'exists' : 'null',
        autoSyncStatus,
        reconnectAttempts: reconnectAttemptsRef.current,
        maxReconnectAttempts
      })
    }
    
    window.getConnectionMetrics = () => {
      const metrics = {
        autoSyncStatus,
        reconnectAttempts: reconnectAttemptsRef.current,
        maxReconnectAttempts,
        hasSubscription: !!subscriptionRef.current,
        incidentsCount: incidents.length,
        unreadCount,
        isRedAlert,
        isBlinking,
        userAuthenticated: !!user,
        userId: user?.id
      }
      
      console.log('📊 Connection Metrics:', metrics)
      return metrics
    }
    
    window.resetConnectionState = () => {
      console.log('🔄 Resetting connection state...')
      
      // Clean up existing subscription
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe()
        subscriptionRef.current = null
      }
      
      // Reset all states
      reconnectAttemptsRef.current = 0
      setAutoSyncStatus('disconnected')
      setIsRedAlert(false)
      setIsBlinking(false)
      
      // Clear timeouts
      if (redAlertTimeoutRef.current) {
        clearTimeout(redAlertTimeoutRef.current)
        redAlertTimeoutRef.current = null
      }
      if (blinkingTimeoutRef.current) {
        clearTimeout(blinkingTimeoutRef.current)
        blinkingTimeoutRef.current = null
      }
      
      console.log('✅ Connection state reset complete')
    }
    
    console.log('🧪 Test functions available:')
    console.log('  - testCriticalAlert(), testHighAlert(), testMediumAlert()')
    console.log('  - forceRedAlert(), stopRedAlert(), checkRedAlertState()')
    
    return () => {
      delete window.testNotification
      delete window.testCriticalAlert
      delete window.testHighAlert
      delete window.testMediumAlert
      delete window.forceRedAlert
      delete window.stopRedAlert
      delete window.checkRedAlertState
      delete window.clearReadIncidents
    }
  }, [isRedAlert, isBlinking])
  
  // Fetch incidents and set up real-time notifications - For all authenticated users
  useEffect(() => {
    if (!user) return
    
    const fetchIncidents = async (retryCount = 0) => {
      const maxRetries = 3;
      const retryDelay = Math.pow(2, retryCount) * 1000; // Exponential backoff: 1s, 2s, 4s
      
      try {
        const { data, error } = await supabase
           .from('incidents')
           .select(`
             id,
             title,
             description,
             incident_type,
             severity,
             status,
             location,
             immediate_actions,
             created_at,
             updated_at,
             reported_by,
             assigned_to
           `)
          .order('created_at', { ascending: false })
          .limit(10)
        
        if (error) {
          // Handle specific Supabase errors
          if (error.code === 'PGRST301' || error.message?.includes('JWT')) {
            console.warn('🔑 Authentication issue, attempting to refresh session...');
            // Try to refresh the session
            const { error: refreshError } = await supabase.auth.refreshSession();
            if (!refreshError && retryCount < maxRetries) {
              console.log('✅ Session refreshed, retrying...');
              return fetchIncidents(retryCount + 1);
            }
          }
          throw error;
        }
        
        setIncidents(data || [])
        console.log('📋 Fetched incidents:', data?.length || 0)
        
        // Count unread incidents (created in last 24 hours for better debugging, not viewed, and not marked as read)
        const twentyFourHoursAgo = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString()
        console.log('🕐 DEBUG: Twenty-four hours ago timestamp:', twentyFourHoursAgo)
        console.log('🕐 DEBUG: Current time:', new Date().toISOString())
        
        const unread = data?.filter(incident => {
          const incidentTime = new Date(incident.created_at)
          const twentyFourHoursAgoTime = new Date(twentyFourHoursAgo)
          const isRecent = incidentTime > twentyFourHoursAgoTime
          const hasValidStatus = ['open', 'submitted', 'reported', 'in_progress', 'under_investigation'].includes(incident.status)
          const isNotRead = !readIncidents.has(incident.id)
          
          console.log(`🔍 DEBUG: Incident ${incident.id}:`, {
            title: incident.title,
            created_at: incident.created_at,
            status: incident.status,
            isRecent,
            hasValidStatus,
            isNotRead,
            shouldCount: isRecent && hasValidStatus && isNotRead
          })
          
          return isRecent && hasValidStatus && isNotRead
        }).length || 0
        
        console.log('🔢 Initial unread count:', unread)
        console.log('📊 DEBUG: Total incidents fetched:', data?.length || 0)
        console.log('📖 DEBUG: Read incidents set size:', readIncidents.size)
        setUnreadCount(unread)
        setIsBlinking(unread > 0)
        
        if (unread > 0) {
          console.log('🔴 Initial blinking state set to true')
        }
      } catch (error) {
        console.error('Error fetching incidents:', error)
        
        // Implement retry logic for network errors
        if (retryCount < maxRetries) {
          const isNetworkError = error.message?.includes('Failed to fetch') || 
                                error.message?.includes('NetworkError') ||
                                error.message?.includes('ERR_CONNECTION_CLOSED') ||
                                error.code === 'NETWORK_ERROR';
          
          if (isNetworkError) {
            console.warn(`🔄 Network error detected, retrying in ${retryDelay}ms... (attempt ${retryCount + 1}/${maxRetries})`);
            setTimeout(() => {
              fetchIncidents(retryCount + 1);
            }, retryDelay);
            return;
          }
        }
        
        // If all retries failed or it's not a network error, set empty incidents
        console.error('❌ Failed to fetch incidents after all retries:', error);
        setIncidents([]);
        setUnreadCount(0);
        setIsBlinking(false);
        
        // Show user-friendly error notification
        setConnectionError('Unable to load incident data. Please check your connection.');
        // Clear error after 5 seconds
        setTimeout(() => setConnectionError(null), 5000);
      }
    }
    
    fetchIncidents()
    
    // Set up real-time subscription for incident changes (INSERT and UPDATE)
    const maxReconnectAttempts = 5
    
    const createSubscription = () => {
      console.log('🔄 Creating real-time subscription...')
      
      // Enhanced channel configuration with better error handling
      const channelName = `incidents_auto_sync_${user?.id || 'anonymous'}_${Date.now()}`
      console.log('📡 Channel name:', channelName)
      
      subscriptionRef.current = supabase
        .channel(channelName, {
          config: {
            presence: {
              key: user?.id || 'anonymous'
            },
            broadcast: {
              self: true
            },
            postgres_changes: {
              enabled: true
            }
          }
        })
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'incidents'
          },
          (payload) => {
            console.log('🔔 New incident submitted via real-time:', payload.new)
            
            // Add new incident to the list with full data
            const newIncident = {
              ...payload.new,
              users: {
                name: payload.new.reporter_name || 'Unknown',
                email: payload.new.reporter_email || 'No email'
              }
            }
            
            setIncidents(prev => [newIncident, ...prev.slice(0, 9)])
            
            // Only increment unread count if this incident is not already marked as read and is recent (within 2 hours)
            const twoHoursAgo = new Date(Date.now() - 2 * 60 * 60 * 1000)
            const incidentTime = new Date(newIncident.created_at)
            
            if (!readIncidents.has(newIncident.id) && incidentTime > twoHoursAgo) {
              setUnreadCount(prev => {
                const newCount = prev + 1
                console.log('📊 Auto-sync: Updated unread count to:', newCount)
                return newCount
              })
            } else {
              console.log('📖 Auto-sync: Incident is either already read or too old, not incrementing count')
            }
            
            // Check if this is a critical/high severity incident for red alert
            const isAlertIncident = payload.new.severity === 'critical' || payload.new.severity === 'high'
            
            if (isAlertIncident) {
              setIsRedAlert(true)
              console.log('🚨 RED ALERT: Critical/High severity incident detected!')
              
              // Clear any existing red alert timeout
              if (redAlertTimeoutRef.current) {
                clearTimeout(redAlertTimeoutRef.current)
              }
              
              // Red alert animation for 20 seconds
              redAlertTimeoutRef.current = setTimeout(() => {
                setIsRedAlert(false)
                console.log('⏰ Red alert animation stopped after 20 seconds')
                redAlertTimeoutRef.current = null
              }, 20000)
            }
            
            setIsBlinking(true)
            console.log('🔴 Auto-sync: Bell icon activated with blinking')
            
            // Clear any existing blinking timeout
            if (blinkingTimeoutRef.current) {
              clearTimeout(blinkingTimeoutRef.current)
            }
            
            // Stop blinking after 15 seconds
            blinkingTimeoutRef.current = setTimeout(() => {
              setIsBlinking(false)
              console.log('⏰ Auto-sync: Blinking stopped after 15 seconds')
              blinkingTimeoutRef.current = null
            }, 15000)
            
            // Show browser notification if permission granted
            if (Notification.permission === 'granted') {
              const notificationTitle = isAlertIncident 
                ? '🚨 CRITICAL SECURITY ALERT 🚨'
                : '🚨 New Security Incident Submitted'
              
              const notificationBody = isAlertIncident
                ? `⚠️ ${payload.new.severity.toUpperCase()} PRIORITY INCIDENT ⚠️\n${payload.new.title}\n\nIMMEDIATE ATTENTION REQUIRED!`
                : `${payload.new.severity.toUpperCase()} Priority: ${payload.new.title}`
              
              new Notification(notificationTitle, {
                body: notificationBody,
                icon: '/favicon.ico',
                tag: 'incident-notification',
                requireInteraction: isAlertIncident,
                silent: false
              })
            }
          }
        )
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'incidents'
          },
          (payload) => {
            console.log('📝 Incident updated via real-time:', payload.new)
            
            // Update existing incident in the list
            setIncidents(prev => 
              prev.map(incident => 
                incident.id === payload.new.id 
                  ? {
                      ...payload.new,
                      users: {
                        name: payload.new.reporter_name || incident.users?.name || 'Unknown',
                        email: payload.new.reporter_email || incident.users?.email || 'No email'
                      }
                    }
                  : incident
              )
            )
            
            // If status changed to submitted, treat as new notification (only if not already read and within 2 hours)
            const twoHoursAgo = new Date(Date.now() - 2 * 60 * 60 * 1000)
            const incidentTime = new Date(payload.new.created_at)
            
            if (payload.old.status !== 'submitted' && 
                payload.new.status === 'submitted' && 
                !readIncidents.has(payload.new.id) &&
                incidentTime > twoHoursAgo) {
              setUnreadCount(prev => prev + 1)
              
              // Check if this is a critical/high severity incident for red alert
              const isAlertIncident = payload.new.severity === 'critical' || payload.new.severity === 'high'
              
              if (isAlertIncident) {
                setIsRedAlert(true)
                console.log('🚨 RED ALERT: Critical/High severity incident status updated!')
                
                // Clear any existing red alert timeout
                if (redAlertTimeoutRef.current) {
                  clearTimeout(redAlertTimeoutRef.current)
                }
                
                // Red alert animation for 20 seconds
                redAlertTimeoutRef.current = setTimeout(() => {
                  setIsRedAlert(false)
                  console.log('⏰ Red alert animation stopped after 20 seconds')
                  redAlertTimeoutRef.current = null
                }, 20000)
              }
              
              setIsBlinking(true)
              console.log('🔄 Auto-sync: Status changed to submitted, triggering notification')
              
              // Clear any existing blinking timeout
              if (blinkingTimeoutRef.current) {
                clearTimeout(blinkingTimeoutRef.current)
              }
              
              blinkingTimeoutRef.current = setTimeout(() => {
                setIsBlinking(false)
                blinkingTimeoutRef.current = null
              }, 15000)
            }
          }
        )
        .subscribe((status, err) => {
          console.log('📡 Auto-sync subscription status:', status)
          
          if (err) {
            console.error('❌ Subscription error details:', err)
          }
          
          switch (status) {
            case 'SUBSCRIBED':
              console.log('✅ Auto-sync: Real-time notifications are active')
              setAutoSyncStatus('connected')
              reconnectAttemptsRef.current = 0 // Reset reconnect attempts on successful connection
              break
              
            case 'CHANNEL_ERROR':
            case 'TIMED_OUT':
            case 'NETWORK_ERROR':
              console.error(`❌ Auto-sync: ${status} - attempting to reconnect...`)
              setAutoSyncStatus('error')
              
              if (reconnectAttemptsRef.current < maxReconnectAttempts) {
                reconnectAttemptsRef.current++
                const backoffDelay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 30000) // Exponential backoff with max 30s
                console.log(`🔄 Reconnect attempt ${reconnectAttemptsRef.current}/${maxReconnectAttempts} in ${backoffDelay}ms`)
                
                setTimeout(() => {
                  if (subscriptionRef.current) {
                    try {
                      subscriptionRef.current.unsubscribe()
                    } catch (unsubError) {
                      console.warn('⚠️ Error during unsubscribe:', unsubError)
                    }
                    subscriptionRef.current = null
                  }
                  createSubscription()
                }, backoffDelay)
              } else {
                console.error('❌ Max reconnection attempts reached')
                setAutoSyncStatus('failed')
                
                // Fallback to periodic sync only
                console.log('🔄 Falling back to periodic sync only')
              }
              break
              
            case 'CLOSED':
              console.warn('⚠️ Auto-sync: Connection closed')
              setAutoSyncStatus('disconnected')
              
              // Only attempt to reconnect if we haven't exceeded max attempts
              if (reconnectAttemptsRef.current < maxReconnectAttempts) {
                reconnectAttemptsRef.current++
                console.log(`🔄 Reconnecting after close... attempt ${reconnectAttemptsRef.current}/${maxReconnectAttempts}`)
                
                setTimeout(() => {
                  createSubscription()
                }, 2000 + (reconnectAttemptsRef.current * 1000)) // Progressive delay
              }
              break
              
            case 'JOINING':
            case 'LEAVING':
              setAutoSyncStatus('connecting')
              break
              
            default:
              console.log(`📡 Subscription status: ${status}`)
              setAutoSyncStatus('connecting')
          }
        })
    }
    
    // Create initial subscription
    createSubscription()
    
    // Request notification permission
    if (Notification.permission === 'default') {
      Notification.requestPermission()
    }
    
    // Set up periodic sync to ensure no incidents are missed
    periodicSyncRef.current = setInterval(() => {
      console.log('🔄 Auto-sync: Performing periodic sync check...')
      fetchIncidents()
      
      // Health check: verify subscription is still active
      if (autoSyncStatus === 'connected' && subscriptionRef.current) {
        // Send a heartbeat to verify connection
        try {
          subscriptionRef.current.send({
            type: 'heartbeat',
            payload: { timestamp: Date.now() }
          })
        } catch (error) {
          console.warn('⚠️ Heartbeat failed, connection may be stale:', error)
          // Force reconnection if heartbeat fails
          window.forceReconnectRealtime()
        }
      } else if (autoSyncStatus === 'failed' || autoSyncStatus === 'error') {
        // Attempt recovery if connection has failed
        console.log('🔄 Attempting recovery from failed connection...')
        window.forceReconnectRealtime()
      }
    }, 30000) // Sync every 30 seconds
    
    // Enhanced connection monitoring
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        console.log('🔄 Auto-sync: Tab became visible, refreshing incidents...')
        fetchIncidents()
        
        // Check connection health when tab becomes visible
        if (autoSyncStatus !== 'connected') {
          console.log('🔄 Tab visible and connection not healthy, attempting reconnection...')
          window.forceReconnectRealtime()
        }
      }
    }
    
    // Network connectivity monitoring
    const handleOnline = () => {
      console.log('🌐 Network connection restored, reconnecting real-time...')
      window.forceReconnectRealtime()
    }
    
    const handleOffline = () => {
      console.log('📴 Network connection lost')
      setAutoSyncStatus('disconnected')
    }
    
    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)
    
    // Cleanup function
     return () => {
       console.log('🧹 Cleaning up real-time subscription and timers...')
       
       if (subscriptionRef.current) {
         subscriptionRef.current.unsubscribe()
         console.log('🧹 Auto-sync: Unsubscribed from real-time notifications')
         subscriptionRef.current = null
       }
       
       if (periodicSyncRef.current) {
         clearInterval(periodicSyncRef.current)
         console.log('🧹 Auto-sync: Cleared periodic sync interval')
         periodicSyncRef.current = null
       }
       
       document.removeEventListener('visibilitychange', handleVisibilityChange)
       window.removeEventListener('online', handleOnline)
       window.removeEventListener('offline', handleOffline)
       
       // Clear any pending timeouts
       if (redAlertTimeoutRef.current) {
         clearTimeout(redAlertTimeoutRef.current)
         redAlertTimeoutRef.current = null
       }
       if (blinkingTimeoutRef.current) {
         clearTimeout(blinkingTimeoutRef.current)
         blinkingTimeoutRef.current = null
       }
       
       // Reset reconnection attempts
       reconnectAttemptsRef.current = 0
       
       console.log('🧹 Auto-sync: Cleaned up subscriptions, intervals, and timeouts')
     }
   }, [user?.id, readIncidents])
   
   // Handle clicking outside dropdowns
   useEffect(() => {
     const handleClickOutside = (event) => {
       if (!event.target.closest('.dropdown-container')) {
         setIsProfileDropdownOpen(false)
         setIsNotificationDropdownOpen(false)
       }
     }
     
     document.addEventListener('mousedown', handleClickOutside)
     return () => {
       document.removeEventListener('mousedown', handleClickOutside)
     }
   }, [])
  
  const getUserTypeLabel = () => {
    return getTypeLabel(profile?.user_type || 'user')
  }
  
  const getUserTypeColor = () => {
    const type = profile?.user_type
    switch (type) {
      case 'it_administrator':
        return 'bg-blue-100 text-blue-800'
      case 'security_officer':
        return 'bg-red-100 text-red-800'
      case 'department_head':
        return 'bg-purple-100 text-purple-800'
      case 'compliance_officer':
        return 'bg-green-100 text-green-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const navigationItems = [
    { path: '/', label: 'Home', icon: Home },
    { path: '/assessment', label: 'Assessment', icon: ClipboardCheck },
    { path: '/dashboard', label: 'Dashboard', icon: BarChart3 },
    ...(user?.user_type === 'admin' || user?.user_type === 'it_administrator' || user?.user_type === 'security_officer' 
      ? [{ path: '/agency-dashboard', label: 'Agency Dashboard', icon: Building2 }] 
      : []),
    ...(user?.user_type === 'admin' || user?.user_type === 'it_administrator' || user?.user_type === 'department_head'
      ? [{ path: '/admin', label: 'Admin Panel', icon: Settings }] 
      : []),
    { path: '/task-reminders', label: 'Task Reminders', icon: Calendar },
    { path: '/policy-library', label: 'Policy Library', icon: FileText },
    { path: '/learning-center', label: 'Learning Center', icon: GraduationCap },
    { path: '/incident-reporter', label: 'Report Incident', icon: AlertTriangle },
    { path: '/incident-dashboard', label: 'Incident Dashboard', icon: Activity }
  ]

  const isActivePath = (path) => {
    return location.pathname === path
  }

  const handleMobileMenuToggle = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen)
  }

  const handleProfileDropdownToggle = () => {
    setIsProfileDropdownOpen(!isProfileDropdownOpen)
    setIsNotificationDropdownOpen(false) // Close notification dropdown
  }

  const handleLogout = async () => {
    await signOut()
    setIsProfileDropdownOpen(false)
  }
  
  const handleNotificationToggle = () => {
    setIsNotificationDropdownOpen(!isNotificationDropdownOpen)
    setIsProfileDropdownOpen(false) // Close profile dropdown
    if (!isNotificationDropdownOpen) {
      // Mark as read when opening dropdown
      setIsBlinking(false)
    }
  }
  
  const formatTimeAgo = (dateString) => {
    const now = new Date()
    const date = new Date(dateString)
    const diffInMinutes = Math.floor((now - date) / (1000 * 60))
    
    if (diffInMinutes < 1) return 'Just now'
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`
    
    const diffInHours = Math.floor(diffInMinutes / 60)
    if (diffInHours < 24) return `${diffInHours}h ago`
    
    const diffInDays = Math.floor(diffInHours / 24)
    return `${diffInDays}d ago`
  }
  
  const getSeverityColor = (severity) => {
    switch (severity?.toLowerCase()) {
      case 'critical':
        return 'bg-red-100 text-red-800 border-red-200'
      case 'high':
        return 'bg-orange-100 text-orange-800 border-orange-200'
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200'
      case 'low':
        return 'bg-green-100 text-green-800 border-green-200'
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200'
    }
  }

  const getSeverityBasedColor = () => {
    // Get unread incidents
    const unreadIncidents = incidents.filter(incident => !readIncidents.has(incident.id))
    
    if (unreadIncidents.length === 0) {
      return 'bg-gradient-to-br from-blue-500 to-blue-600 hover:scale-105 shadow-blue-500/30'
    }
    
    // Find the highest severity among unread incidents
    const severityPriority = { 'critical': 4, 'high': 3, 'medium': 2, 'low': 1 }
    const highestSeverity = unreadIncidents.reduce((highest, incident) => {
      const currentPriority = severityPriority[incident.severity?.toLowerCase()] || 0
      const highestPriority = severityPriority[highest?.toLowerCase()] || 0
      return currentPriority > highestPriority ? incident.severity : highest
    }, 'low')
    
    // Return color and animation based on highest severity
    switch (highestSeverity?.toLowerCase()) {
      case 'critical':
        return isRedAlert 
          ? 'bg-gradient-to-br from-yellow-400 to-orange-500 animate-pulse scale-110 shadow-yellow-500/50' 
          : 'bg-gradient-to-br from-red-600 to-red-700 scale-105 shadow-red-600/40'
      case 'high':
        return isBlinking 
          ? 'bg-gradient-to-br from-orange-500 to-red-500 animate-bounce scale-105 shadow-orange-500/40'
          : 'bg-gradient-to-br from-orange-500 to-orange-600 hover:scale-105 shadow-orange-500/30'
      case 'medium':
        return isBlinking 
          ? 'bg-gradient-to-br from-yellow-500 to-orange-400 animate-bounce scale-105 shadow-yellow-500/40'
          : 'bg-gradient-to-br from-yellow-500 to-yellow-600 hover:scale-105 shadow-yellow-500/30'
      case 'low':
        return isBlinking 
          ? 'bg-gradient-to-br from-green-500 to-blue-500 animate-bounce scale-105 shadow-green-500/40'
          : 'bg-gradient-to-br from-green-500 to-green-600 hover:scale-105 shadow-green-500/30'
      default:
        return 'bg-gradient-to-br from-blue-500 to-blue-600 hover:scale-105 shadow-blue-500/30'
    }
  }
  
  const clearNotifications = () => {
    if (!user?.id) {
      console.warn('⚠️ Cannot clear notifications: No user ID')
      return
    }
    
    console.log('🧹 Clearing all notifications for user:', user.id)
    
    // Mark all current incidents as read with timestamps
    const currentIncidentIds = incidents.map(incident => incident.id)
    const newReadIncidents = new Set([...readIncidents, ...currentIncidentIds])
    setReadIncidents(newReadIncidents)
    
    // Store in localStorage for persistence with user-specific key and timestamps
    const userSpecificKey = `readIncidents_${user.id}`
    const existingData = JSON.parse(localStorage.getItem(userSpecificKey) || '[]')
    const newReadData = currentIncidentIds.map(id => ({
      id,
      timestamp: Date.now()
    }))
    
    const updatedData = [...existingData, ...newReadData]
    localStorage.setItem(userSpecificKey, JSON.stringify(updatedData))
    
    setUnreadCount(0)
    setIsBlinking(false)
    setIsNotificationDropdownOpen(false) // Close the dropdown after clearing
    
    console.log('✅ All notifications cleared. Marked', currentIncidentIds.length, 'incidents as read')
  }
  
  const handleIncidentClick = (incident) => {
    if (!user?.id) return
    
    // Mark this incident as read
    const newReadIncidents = new Set([...readIncidents, incident.id])
    setReadIncidents(newReadIncidents)
    
    // Store in localStorage for persistence with user-specific key and timestamp
    const userSpecificKey = `readIncidents_${user.id}`
    const existingData = JSON.parse(localStorage.getItem(userSpecificKey) || '[]')
    const newReadData = { id: incident.id, timestamp: Date.now() }
    
    const updatedData = [...existingData, newReadData]
    localStorage.setItem(userSpecificKey, JSON.stringify(updatedData))
    
    // Recalculate unread count (using 2-hour window to match notification logic)
    const twoHoursAgo = new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString()
    const unread = incidents.filter(inc => {
      const incidentTime = new Date(inc.created_at)
      const twoHoursAgoTime = new Date(twoHoursAgo)
      
      return incidentTime > twoHoursAgoTime &&
             (inc.status === 'open' || inc.status === 'submitted' || inc.status === 'reported') &&
             !newReadIncidents.has(inc.id)
    }).length
    
    setUnreadCount(unread)
    console.log('📖 Marked incident as read:', incident.id, 'New unread count:', unread)
    
    setSelectedIncident(incident)
    setShowIncidentModal(true)
    setIsNotificationDropdownOpen(false)
  }
  
  const closeIncidentModal = () => {
    setShowIncidentModal(false)
    setSelectedIncident(null)
  }
  
  const formatIncidentType = (type) => {
    if (!type) return 'Unknown'
    return type.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ')
  }
  
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  return (
    <>
      {/* Connection Error Notification */}
      <AnimatePresence>
        {connectionError && (
          <motion.div
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -50 }}
            className="bg-red-500 text-white px-4 py-2 text-sm font-medium text-center relative z-50"
          >
            <div className="flex items-center justify-center space-x-2">
              <AlertTriangle size={16} />
              <span>{connectionError}</span>
              <button
                onClick={() => setConnectionError(null)}
                className="ml-2 hover:bg-red-600 rounded p-1"
              >
                <X size={14} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      <nav className="bg-gradient-to-r from-gov-50 to-gov-100 shadow-lg border-b border-gov-200 sticky top-0 z-50">
        <div className="max-w-full mx-auto px-6 sm:px-8 lg:px-12">
        <div className="flex items-center h-18 max-w-screen-2xl mx-auto">
          {/* Logo Section */}
          <div className="flex items-center w-1/5 flex-shrink-0">
            <Link to="/" className="flex items-center space-x-2 group">
              <div className="flex items-center justify-center w-10 h-10 bg-gradient-to-br from-cyber-600 to-cyber-700 rounded-lg shadow-md group-hover:shadow-lg transition-all duration-300">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <div className="hidden sm:block">
                <h1 className="text-lg font-display font-bold text-gov-800 leading-tight">
                  CyberGov <span className="text-cyber-600">Check</span>
                </h1>
                <p className="text-xs text-gov-500 font-medium tracking-wide">Philippine Government Cybersecurity</p>
              </div>
            </Link>
          </div>

          {/* Desktop Navigation - Centered */}
          <div className="hidden lg:flex items-center justify-center flex-1">
            <div className="flex items-center space-x-1 bg-white/60 backdrop-blur-sm rounded-2xl p-4 shadow-sm border border-white/40 w-full">
              {navigationItems.map((item) => {
                const Icon = item.icon
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`flex items-center space-x-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all duration-300 whitespace-nowrap flex-1 justify-center ${
                      isActivePath(item.path)
                        ? 'bg-white text-cyber-700 shadow-lg border border-cyber-300/50 transform scale-105'
                        : 'text-gov-700 hover:text-cyber-600 hover:bg-white/80 hover:shadow-md hover:scale-102'
                    }`}
                  >
                    <Icon size={16} className="flex-shrink-0" />
                    <span className="font-medium text-xs">{item.label}</span>
                  </Link>
                )
              })}
            </div>
          </div>

          {/* Medium screens navigation */}
          <div className="hidden md:flex lg:hidden items-center justify-center flex-1 space-x-1">
            {navigationItems.slice(0, 4).map((item) => {
              const Icon = item.icon
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center justify-center p-2.5 rounded-lg transition-all duration-200 ${
                    isActivePath(item.path)
                      ? 'bg-cyber-50 text-cyber-700 border border-cyber-200'
                      : 'text-gov-600 hover:text-cyber-600 hover:bg-cyber-50'
                  }`}
                  title={item.label}
                >
                  <Icon size={18} />
                </Link>
              )
            })}
          </div>

          {/* Right Section - User Profile & Actions */}
          <div className="flex items-center justify-end space-x-2 w-1/5 flex-shrink-0">
            {/* Notifications - Show for all users, but animations only for admin */}
            <div className="relative dropdown-container">
              <button 
                onClick={handleNotificationToggle}
                className="relative p-3 rounded-xl transition-all duration-300 flex items-center justify-center group text-gov-600 hover:text-cyber-600 hover:bg-gray-100"
                aria-label={`Notifications ${unreadCount > 0 ? `(${unreadCount} unread)` : ''}`}
              >
                <div className="relative">
                  <Bell 
                    size={20} 
                    className="transition-all duration-300 group-hover:scale-110" 
                  />
                  
                  {/* Enhanced notification badge */}
                  {unreadCount > 0 && (profile?.user_type === 'it_administrator' || profile?.user_type === 'security_officer' || profile?.user_type === 'department_head') && (
                    <div className="absolute -top-3 -right-3">
                      <span className={`relative inline-flex items-center justify-center min-w-[22px] h-[22px] text-white text-xs font-bold rounded-full border-2 border-white shadow-lg transition-all duration-300 ${
                        getSeverityBasedColor()
                      }`}>
                        <span className={`relative z-10 ${
                          isRedAlert ? 'animate-pulse text-shadow-sm' : ''
                        }`}>
                          {unreadCount > 999 ? '999+' : unreadCount}
                        </span>
                        
                        {/* Pulsing ring for critical alerts */}
                        {isRedAlert && (
                          <span className="absolute inset-0 rounded-full bg-yellow-400 animate-ping opacity-75"></span>
                        )}
                      </span>
                    </div>
                  )}
                </div>

              </button>
              
              {/* Notification Dropdown */}
              <AnimatePresence>
                {isNotificationDropdownOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -10, scale: 0.95 }}
                    className="absolute right-0 mt-2 w-96 bg-white rounded-lg shadow-xl border border-gov-200 py-2 z-50 max-h-96 overflow-hidden"
                  >
                    {/* Header */}
                    <div className="flex items-center justify-between px-4 py-2 border-b border-gov-100">
                      <h3 className="text-sm font-semibold text-gov-800">Security Incidents</h3>
                      <div className="flex items-center space-x-2">
                        {(() => {
                          // Enhanced debugging for button visibility
                          const hasUnreadIncidents = unreadCount > 0
                          const hasIncidents = incidents.length > 0
                          const userCanSeeNotifications = profile?.user_type === 'it_administrator' || 
                                                         profile?.user_type === 'security_officer' || 
                                                         profile?.user_type === 'department_head'
                          
                          console.log('🔍 DEBUG: Mark all read button visibility check:', {
                            unreadCount,
                            hasUnreadIncidents,
                            hasIncidents,
                            userType: profile?.user_type,
                            userCanSeeNotifications,
                            readIncidentsSize: readIncidents.size,
                            shouldShowButton: hasUnreadIncidents || hasIncidents // Show if there are any incidents, not just unread
                          })
                          
                          // Show button if there are any incidents (not just unread) and user has permission
                          return (hasUnreadIncidents || hasIncidents) && userCanSeeNotifications
                        })() && (
                          <button
                            onClick={clearNotifications}
                            className="text-xs text-cyber-600 hover:text-cyber-700 font-medium"
                          >
                            Mark all read
                          </button>
                        )}
                        <Link
                          to="/incident-dashboard"
                          onClick={() => setIsNotificationDropdownOpen(false)}
                          className="text-xs text-cyber-600 hover:text-cyber-700 font-medium flex items-center space-x-1"
                        >
                          <Eye size={12} />
                          <span>View All</span>
                        </Link>
                      </div>
                    </div>
                    
                    {/* Auto-Sync Status Indicator */}
                    <div className="px-4 py-2 bg-gray-50 border-b border-gov-100">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <div className={`w-2 h-2 rounded-full ${
                            autoSyncStatus === 'connected' ? 'bg-green-500 animate-pulse' :
                            autoSyncStatus === 'connecting' || autoSyncStatus === 'reconnecting' ? 'bg-yellow-500 animate-pulse' :
                            autoSyncStatus === 'error' ? 'bg-red-500' :
                            'bg-gray-400'
                          }`}></div>
                          <span className="text-xs text-gov-600">
                            Auto-sync: {
                              autoSyncStatus === 'connected' ? 'Active' :
                              autoSyncStatus === 'connecting' ? 'Connecting...' :
                              autoSyncStatus === 'reconnecting' ? 'Reconnecting...' :
                              autoSyncStatus === 'error' ? 'Error' :
                              'Disconnected'
                            }
                          </span>
                        </div>
                        <span className="text-xs text-gov-400">Real-time notifications</span>
                      </div>
                    </div>
                    
                    {/* Incidents List */}
                    <div className="max-h-80 overflow-y-auto">
                      {incidents.length > 0 ? (
                         incidents.map((incident, index) => {
                           const isUnread = !readIncidents.has(incident.id); // Check if incident has been read
                           return (
                           <button
                             key={incident.id}
                             onClick={() => handleIncidentClick(incident)}
                             className={`w-full text-left px-4 py-3 border-b border-gov-50 last:border-b-0 transition-all duration-300 ${
                               isUnread 
                                 ? 'bg-gradient-to-r from-cyber-50 to-blue-50 hover:from-cyber-75 hover:to-blue-75 border-l-4 border-l-cyber-500 shadow-sm' 
                                 : 'hover:bg-gov-25'
                             }`}
                           >
                            <div className="flex items-start justify-between">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center space-x-2 mb-1">
                                  {isUnread && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-cyber-500 text-white animate-pulse">
                                      NEW
                                    </span>
                                  )}
                                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${
                                    getSeverityColor(incident.severity)
                                  }`}>
                                    {incident.severity}
                                  </span>
                                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                    incident.status === 'open' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'
                                  }`}>
                                    {incident.status}
                                  </span>
                                </div>
                                <p className="text-sm font-medium text-gov-800 truncate">
                                  {incident.title}
                                </p>
                                <p className="text-xs text-gov-500 mt-1">
                                  Reported by {incident.users?.name || 'Unknown'}
                                </p>
                              </div>
                              <div className="flex items-center space-x-1 text-xs text-gov-400 ml-2">
                                <Clock size={12} />
                                <span>{formatTimeAgo(incident.created_at)}</span>
                              </div>
                             </div>
                           </button>
                           )
                         })
                      ) : (
                        <div className="px-4 py-8 text-center">
                          <AlertTriangle size={24} className="mx-auto text-gov-300 mb-2" />
                          <p className="text-sm text-gov-500">No recent incidents</p>
                          <p className="text-xs text-gov-400 mt-1">All clear! No security incidents reported.</p>
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
             </div>
             
             {/* Incident Preview Modal */}
             <AnimatePresence>
               {showIncidentModal && selectedIncident && (
                 <motion.div
                   initial={{ opacity: 0 }}
                   animate={{ opacity: 1 }}
                   exit={{ opacity: 0 }}
                   className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4"
                   onClick={closeIncidentModal}
                 >
                   <motion.div
                     initial={{ opacity: 0, scale: 0.9, y: 20 }}
                     animate={{ opacity: 1, scale: 1, y: 0 }}
                     exit={{ opacity: 0, scale: 0.9, y: 20 }}
                     className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden"
                     onClick={(e) => e.stopPropagation()}
                   >
                     {/* Modal Header */}
                     <div className="bg-gradient-to-r from-cyber-600 to-cyber-700 px-6 py-4 text-white">
                       <div className="flex items-center justify-between">
                         <div className="flex items-center space-x-3">
                           <AlertTriangle size={24} />
                           <div>
                             <h2 className="text-xl font-bold">Security Incident Details</h2>
                             <p className="text-cyber-100 text-sm">ID: {selectedIncident.id}</p>
                           </div>
                         </div>
                         <button
                           onClick={closeIncidentModal}
                           className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                         >
                           <X size={20} />
                         </button>
                       </div>
                     </div>
                     
                     {/* Modal Content */}
                     <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
                       {/* Status and Severity */}
                       <div className="flex items-center space-x-4 mb-6">
                         <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${
                           getSeverityColor(selectedIncident.severity)
                         }`}>
                           {selectedIncident.severity}
                         </span>
                         <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                           selectedIncident.status === 'open' ? 'bg-yellow-100 text-yellow-800' : 
                           selectedIncident.status === 'investigating' ? 'bg-blue-100 text-blue-800' :
                           selectedIncident.status === 'resolved' ? 'bg-green-100 text-green-800' :
                           'bg-gray-100 text-gray-800'
                         }`}>
                           {selectedIncident.status}
                         </span>
                         <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800">
                           {formatIncidentType(selectedIncident.incident_type)}
                         </span>
                       </div>
                       
                       {/* Title */}
                       <h3 className="text-2xl font-bold text-gov-800 mb-4">
                         {selectedIncident.title}
                       </h3>
                       
                       {/* Description */}
                       {selectedIncident.description && (
                         <div className="mb-6">
                           <h4 className="text-lg font-semibold text-gov-700 mb-2">Description</h4>
                           <div className="bg-gov-25 rounded-lg p-4">
                             <p className="text-gov-600 leading-relaxed whitespace-pre-wrap">
                               {selectedIncident.description}
                             </p>
                           </div>
                         </div>
                       )}
                       
                       {/* Location */}
                       {selectedIncident.location && (
                         <div className="mb-6">
                           <h4 className="text-lg font-semibold text-gov-700 mb-2">Location</h4>
                           <p className="text-gov-600 bg-gov-25 rounded-lg p-3">
                             {selectedIncident.location}
                           </p>
                         </div>
                       )}
                       
                       {/* Immediate Actions */}
                       {selectedIncident.immediate_actions && (
                         <div className="mb-6">
                           <h4 className="text-lg font-semibold text-gov-700 mb-2">Immediate Actions Taken</h4>
                           <div className="bg-blue-25 rounded-lg p-4">
                             <p className="text-blue-700 leading-relaxed whitespace-pre-wrap">
                               {selectedIncident.immediate_actions}
                             </p>
                           </div>
                         </div>
                       )}
                       
                       {/* Reporter and Assignment Info */}
                       <div className="grid md:grid-cols-2 gap-6 mb-6">
                         <div>
                           <h4 className="text-lg font-semibold text-gov-700 mb-2">Reported By</h4>
                           <div className="bg-gov-25 rounded-lg p-4">
                             <p className="font-medium text-gov-800">
                               {selectedIncident.users?.name || 'Unknown'}
                             </p>
                             <p className="text-sm text-gov-500">
                               {selectedIncident.users?.email || 'No email'}
                             </p>
                           </div>
                         </div>
                         
                         {selectedIncident.assigned_user && (
                           <div>
                             <h4 className="text-lg font-semibold text-gov-700 mb-2">Assigned To</h4>
                             <div className="bg-cyber-25 rounded-lg p-4">
                               <p className="font-medium text-cyber-800">
                                 {selectedIncident.assigned_user.name}
                               </p>
                               <p className="text-sm text-cyber-600">
                                 {selectedIncident.assigned_user.email}
                               </p>
                             </div>
                           </div>
                         )}
                       </div>
                       
                       {/* Timeline */}
                       <div className="mb-6">
                         <h4 className="text-lg font-semibold text-gov-700 mb-3">Timeline</h4>
                         <div className="space-y-3">
                           <div className="flex items-center space-x-3 p-3 bg-green-25 rounded-lg">
                             <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                             <div>
                               <p className="text-sm font-medium text-green-800">Incident Reported</p>
                               <p className="text-xs text-green-600">{formatDate(selectedIncident.created_at)}</p>
                             </div>
                           </div>
                           
                           {selectedIncident.updated_at !== selectedIncident.created_at && (
                             <div className="flex items-center space-x-3 p-3 bg-blue-25 rounded-lg">
                               <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                               <div>
                                 <p className="text-sm font-medium text-blue-800">Last Updated</p>
                                 <p className="text-xs text-blue-600">{formatDate(selectedIncident.updated_at)}</p>
                               </div>
                             </div>
                           )}
                         </div>
                       </div>
                       
                       {/* Action Buttons */}
                       <div className="flex justify-end space-x-3 pt-4 border-t border-gov-200">
                         <button
                           onClick={closeIncidentModal}
                           className="px-4 py-2 text-gov-600 hover:text-gov-800 hover:bg-gov-50 rounded-lg transition-colors"
                         >
                           Close
                         </button>
                         <Link
                           to={`/incident-dashboard?incident=${selectedIncident.id}`}
                           onClick={closeIncidentModal}
                           className="px-6 py-2 bg-cyber-600 text-white hover:bg-cyber-700 rounded-lg transition-colors font-medium"
                         >
                           View in Dashboard
                         </Link>
                       </div>
                     </div>
                   </motion.div>
                 </motion.div>
               )}
             </AnimatePresence>
 
             {/* User Profile Dropdown */}
             <div className="relative dropdown-container">
              <button
                onClick={handleProfileDropdownToggle}
                className="flex items-center space-x-2 px-2 py-2 rounded-lg hover:bg-gov-50 transition-all duration-200 border border-transparent hover:border-gov-200"
              >
                <div className="w-8 h-8 bg-gradient-to-br from-cyber-100 to-cyber-200 rounded-full flex items-center justify-center ring-1 ring-cyber-200 shadow-sm">
                  <User size={14} className="text-cyber-600" />
                </div>
                <div className="hidden sm:block text-left">
                  <p className="text-sm font-semibold text-gov-800 leading-tight">
                    {profile?.name || user?.email || 'Guest User'}
                  </p>
                  <p className={`text-xs px-2 py-0.5 rounded-full font-medium ${getUserTypeColor()}`}>
                    {getUserTypeLabel()}
                  </p>
                </div>
                <ChevronDown size={12} className="text-gov-500" />
              </button>

              {/* Profile Dropdown Menu */}
              <AnimatePresence>
                {isProfileDropdownOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gov-200 py-1"
                  >
                    <Link
                      to="/profile"
                      className="flex items-center space-x-2 px-4 py-2 text-sm text-gov-700 hover:bg-gov-50"
                      onClick={() => setIsProfileDropdownOpen(false)}
                    >
                      <User size={16} />
                      <span>Profile</span>
                    </Link>
                    <Link
                      to="/profile"
                      className="flex items-center space-x-2 px-4 py-2 text-sm text-gov-700 hover:bg-gov-50"
                      onClick={() => setIsProfileDropdownOpen(false)}
                    >
                      <Settings size={16} />
                      <span>Settings</span>
                    </Link>
                    <hr className="my-1 border-gov-200" />
                    <button
                      onClick={handleLogout}
                      className="flex items-center space-x-2 w-full px-4 py-2 text-sm text-security-600 hover:bg-security-50"
                    >
                      <LogOut size={16} />
                      <span>Logout</span>
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={handleMobileMenuToggle}
              className="md:hidden p-2.5 text-gov-600 hover:text-cyber-600 hover:bg-cyber-50 rounded-lg transition-all duration-200 border border-transparent hover:border-gov-200"
            >
              {isMobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-gradient-to-b from-white to-gov-25 border-t border-gov-200 shadow-lg"
          >
            <div className="px-4 py-4 space-y-2">
              {navigationItems.map((item) => {
                const Icon = item.icon
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`flex items-center space-x-4 px-4 py-3.5 rounded-xl text-sm font-medium transition-all duration-200 ${
                      isActivePath(item.path)
                        ? 'bg-white text-cyber-700 border border-cyber-200 shadow-md'
                        : 'text-gov-600 hover:text-cyber-600 hover:bg-white/70 hover:shadow-sm'
                    }`}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <div className={`p-2 rounded-lg ${
                      isActivePath(item.path)
                        ? 'bg-cyber-100'
                        : 'bg-gov-100'
                    }`}>
                      <Icon size={18} />
                    </div>
                    <span className="font-semibold">{item.label}</span>
                  </Link>
                )
              })}
              
              {/* Mobile User Section */}
              <div className="pt-4 mt-4 border-t border-gov-200">
                <div className="flex items-center space-x-4 px-4 py-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-cyber-100 to-cyber-200 rounded-full flex items-center justify-center">
                    <User size={18} className="text-cyber-600" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gov-800">
                      {profile?.name || user?.email || 'Guest User'}
                    </p>
                    <p className={`text-xs px-2 py-0.5 rounded-full font-medium ${getUserTypeColor()}`}>
                      {getUserTypeLabel()}
                    </p>
                  </div>
                </div>
                <div className="px-4 space-y-1">
                  <Link
                    to="/profile"
                    className="flex items-center space-x-3 px-3 py-2 text-sm text-gov-600 hover:text-cyber-600 hover:bg-white/70 rounded-lg transition-all duration-200"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <Settings size={16} />
                    <span>Settings</span>
                  </Link>
                  <button
                    onClick={() => {
                      handleLogout()
                      setIsMobileMenuOpen(false)
                    }}
                    className="flex items-center space-x-3 w-full px-3 py-2 text-sm text-security-600 hover:bg-security-50 rounded-lg transition-all duration-200"
                  >
                    <LogOut size={16} />
                    <span>Logout</span>
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
    </>
  )
}

export default Navbar